import { createSlice } from '@reduxjs/toolkit';
import { PunchState } from './punchTypes';
import {
  createPunch,
  getPunchById,
  getPunchByEmployeeId,
  updatePunch,
  deletePunch,
  getEmployeePresenceCount, // Add the action for employee presence count
  getPunchByDate,
  getEmployeeAttendanceByDate,
  getPunchByEmployeeWithId
} from './punchAction';

// Initial State
const initialState: PunchState = {
  punches: [],
  punchess : [],
  selectedPunch: undefined,
  punchesByDate: [],
  presentEmployee: undefined, // <-- Added for storing the presence data
  employeeAttendanceAnalytics: undefined, 
  loadingStates: {
    create: false,
    update: false,
    delete: false,
    fetchById: false,
    fetchByDate: false, 
    fetchByEmployeeId: false,
    fetchPresenceCount: false, // 
    fetchAttendanceByDate: false, 
  },
  errors: {
    create: undefined,
    update: undefined,
    delete: undefined,
    fetchById: undefined,
    fetchByEmployeeId: undefined,
    fetchByDate: undefined,
    fetchPresenceCount: undefined, 
    fetchAttendanceByDate: undefined, 
  },
};

// Slice
const punchSlice = createSlice({
  name: 'punches',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Create Punch
      .addCase(createPunch.pending, (state) => {
        state.loadingStates.create = true;
        state.errors.create = undefined;
      })
      .addCase(createPunch.fulfilled, (state, action) => {
        state.loadingStates.create = false;
        state.punches.push(action.payload);
      })
      .addCase(createPunch.rejected, (state, action) => {
        state.loadingStates.create = false;
        state.errors.create = action.payload as string;
      })

      // Get Punch by ID
      .addCase(getPunchById.pending, (state) => {
        state.loadingStates.fetchById = true;
        state.errors.fetchById = undefined;
      })
      .addCase(getPunchById.fulfilled, (state, action) => {
        state.loadingStates.fetchById = false;
        state.selectedPunch = action.payload;
      })
      .addCase(getPunchById.rejected, (state, action) => {
        state.loadingStates.fetchById = false;
        state.errors.fetchById = action.payload as string;
      })
      .addCase(getEmployeeAttendanceByDate.pending, (state) => {
        state.loadingStates.fetchAttendanceByDate = true;
        state.errors.fetchAttendanceByDate = undefined;
      })
      .addCase(getEmployeeAttendanceByDate.fulfilled, (state, action) => {
        state.loadingStates.fetchAttendanceByDate = false;
        state.employeeAttendanceAnalytics = action.payload.data; // Save analytics data
      })
      .addCase(getEmployeeAttendanceByDate.rejected, (state, action) => {
        state.loadingStates.fetchAttendanceByDate = false;
        state.errors.fetchAttendanceByDate = action.payload as string;
      })

      // Get Punches by Employee ID
      .addCase(getPunchByEmployeeId.pending, (state) => {
        state.loadingStates.fetchByEmployeeId = true;
        state.errors.fetchByEmployeeId = undefined;
      })
      .addCase(getPunchByEmployeeId.fulfilled, (state, action) => {
        state.loadingStates.fetchByEmployeeId = false;
        state.punches = action.payload.data;
      })
      .addCase(getPunchByEmployeeId.rejected, (state, action) => {
        state.loadingStates.fetchByEmployeeId = false;
        state.errors.fetchByEmployeeId = action.payload as string;
      })
      // Get Punches by Employee ID
      .addCase(getPunchByEmployeeWithId.pending, (state) => {
        state.loadingStates.fetchByEmployeeId = true;
        state.errors.fetchByEmployeeId = undefined;
      })
      .addCase(getPunchByEmployeeWithId.fulfilled, (state, action) => {
        state.loadingStates.fetchByEmployeeId = false;
        state.punchess = action.payload.data;
      })
      .addCase(getPunchByEmployeeWithId.rejected, (state, action) => {
        state.loadingStates.fetchByEmployeeId = false;
        state.errors.fetchByEmployeeId = action.payload as string;
      })

      // Get Punches by Date
      .addCase(getPunchByDate.pending, (state) => {
        state.loadingStates.fetchByDate = true;
        state.errors.fetchByDate = undefined;
      })
      .addCase(getPunchByDate.fulfilled, (state, action) => {
        state.loadingStates.fetchByDate = false;
        state.punchesByDate = action.payload.data; 
      })
      .addCase(getPunchByDate.rejected, (state, action) => {
        state.loadingStates.fetchByDate = false;
        state.errors.fetchByDate = action.payload as string;
      })

      // Update Punch
      .addCase(updatePunch.pending, (state) => {
        state.loadingStates.update = true;
        state.errors.update = undefined;
      })
      .addCase(updatePunch.fulfilled, (state, action) => {
        state.loadingStates.update = false;
        const index = state.punches.findIndex((punch) => punch.Id === action.payload.Id);
        if (index !== -1) {
          state.punches[index] = action.payload;
        }
      })
      .addCase(updatePunch.rejected, (state, action) => {
        state.loadingStates.update = false;
        state.errors.update = action.payload as string;
      })

      // Delete Punch
      .addCase(deletePunch.pending, (state) => {
        state.loadingStates.delete = true;
        state.errors.delete = undefined;
      })
      .addCase(deletePunch.fulfilled, (state, action) => {
        state.loadingStates.delete = false;
        state.punches = state.punches.filter((punch) => punch.Id !== action.payload.Id);
      })
      .addCase(deletePunch.rejected, (state, action) => {
        state.loadingStates.delete = false;
        state.errors.delete = action.payload as string;
      })

      // Get Employee Presence Count
      .addCase(getEmployeePresenceCount.pending, (state) => {
        state.loadingStates.fetchPresenceCount = true;
        state.errors.fetchPresenceCount = undefined;
      })
      .addCase(getEmployeePresenceCount.fulfilled, (state, action) => {
        state.loadingStates.fetchPresenceCount = false;
        state.presentEmployee = action.payload.data; 
      })
      .addCase(getEmployeePresenceCount.rejected, (state, action) => {
        state.loadingStates.fetchPresenceCount = false;
        state.errors.fetchPresenceCount = action.payload as string;
      });
  },
});

export default punchSlice.reducer;
